import * as React from 'react';
import { LOCAL_STORAGE_AUTH_ITEM, LOCAL_STORAGE_AUTH_URL } from '../constants';
import TokenRefreshProps from './TokenRefreshProps.interface';

// Set to TRUE to use the Logout Url once changes to IDP has been deployed to Upper Environments
const useLogoutUrl = true;

// Minimum number of minutes before expiration that Token will be refreshed
const expireMinutesThreshold = 5;

const iFrameId = 'tokenRefresh';

const TokenRefresh = ({
  loginUrl,
  logoutUrl,
  tokenRefreshUrl,
  tokenRequestUrl,
}: TokenRefreshProps): JSX.Element => {
  const [expireTimeout, setExpireTimeout] = React.useState(null);

  // Formats the Duration ina muman readable form, e.g 12m 34s
  const formatDuration = (totalSeconds: number): string => {
    const hours: number = Math.floor(totalSeconds / 3600);
    const minutes: number = Math.floor((totalSeconds % 3600) / 60);
    const seconds: number = Math.floor(totalSeconds - hours * 3600 - minutes * 60);

    return [`${hours}h`, `${minutes}m`, `${seconds}s`]
      .filter((item) => item[0] !== '0')
      .join(' ');
  };

  // Creates the IFrame Element (Done after the Component has mounted)
  const createIFrame = (): void => {
    const iframe: HTMLIFrameElement = document.createElement('iframe');
    iframe.setAttribute('id', iFrameId);
    iframe.setAttribute('style', 'display: none;');

    document.body.appendChild(iframe);
  };

  // Starts the Token Refresh by setting the Source of the IFrame
  const refreshToken = (): void => {
    const element: HTMLIFrameElement = document.getElementById(iFrameId) as HTMLIFrameElement;
    element.src = tokenRefreshUrl;
  };

  // Oh well we tried. The Token could not be updated.  Perform a Logout
  const logout = (checkTokenAgain = true): void => {
    // Get the Token from the Local Storage
    const authCache = localStorage.getItem(LOCAL_STORAGE_AUTH_ITEM) && JSON.parse(localStorage.getItem(LOCAL_STORAGE_AUTH_ITEM));

    // Check the Token Again before Logging Out.
    // This condition may happen if the UI starts up with a Token that is valid for less than 1min. There was not enough time to reset Logout Timer
    if (checkTokenAgain) {
      // Token is Valid
      if (authCache) {
        const now = new Date().getTime();

        // If the expiration is within 5 sec there is nothing else to do.  Log out
        if (authCache.expires < (now + (5 * 1000))) {
          localStorage.removeItem(LOCAL_STORAGE_AUTH_ITEM);
          window.location.href = useLogoutUrl ? logoutUrl : loginUrl;
        }
      }
    } else { // Logout without checking
      localStorage.removeItem(LOCAL_STORAGE_AUTH_ITEM);
      window.location.href = useLogoutUrl ? logoutUrl : loginUrl;
    }
  };

  // Check if the Token has Expired
  const checkTokenExpiration = () => {
    // Get the Token from the Local Storage
    const authCache = localStorage.getItem(LOCAL_STORAGE_AUTH_ITEM) && JSON.parse(localStorage.getItem(LOCAL_STORAGE_AUTH_ITEM));

    // Token is Valid
    if (authCache) {
      const now = new Date().getTime();

      // Token has already Expired before it could be refreshed
      if (authCache.expires < now) {
        // eslint-disable-next-line no-console
        console.log('Token Already Expired');

        logout(false);
      } else { // Token has not Expired
        // Save the Expire Timeout
        setExpireTimeout(authCache.expires);

        // Check if the Token will expire soon
        const isTokenAboutToExpire = (authCache.expires - now) < (expireMinutesThreshold * 60 * 1000);

        if (isTokenAboutToExpire) {
          // Store the Token Request Url
          localStorage.setItem(LOCAL_STORAGE_AUTH_URL, tokenRequestUrl);

          // eslint-disable-next-line no-console
          console.log('Token Expiring Soon:', formatDuration((authCache.expires - now) / 1000), new Date(authCache.expires));

          // Refresh the Token
          refreshToken();
        }
      }
    } else { // Token is Invalid
      logout();
    }
  };

  // Create a fail save Logout Timer - If the Token is successfully updated we should never have to Logout
  React.useEffect(() => {
    const now = new Date().getTime();

    if (expireTimeout) {
      // eslint-disable-next-line no-console
      console.log('Set Logout Timer:', formatDuration((expireTimeout - now) / 1000), new Date(expireTimeout));
    }

    // Create the Logout Timer
    const logoutTimer = expireTimeout ? setTimeout(logout, expireTimeout - now) : null;

    return (): void => {
      if (logoutTimer) {
        // eslint-disable-next-line no-console
        console.log('Reset Logout Timer');

        // Clear the Logout Timer
        clearTimeout(logoutTimer);
      }
    };
  }, [expireTimeout]);

  // Interval Timer
  React.useEffect(() => {
    // Create the Initial IFrame when the Component Mounts
    createIFrame();

    // Immediately check for Token Expiration
    checkTokenExpiration();

    // Create a Refresh Timer (1 min) to check for Token Expiration
    const checkIntervalMinutes = 1;
    const refreshTimer = setInterval(checkTokenExpiration, checkIntervalMinutes * 60 * 1000);

    return (): void => {
      // Clears the Refresh Timer when the Component Unmounts
      clearInterval(refreshTimer);
    };
  }, []);

  return (
    <></>
  );
};

export default TokenRefresh;
